import React, {
  useState,
  useEffect,
  useRef,
  useReducer,
  useCallback,
} from "react";
import { marked } from "marked"; // For rendering the report if it's Markdown
import * as Icon from "react-icons/md";

// --- Constants ---
// Replace with your actual chat API endpoint if different
const CHAT_API_URL = "https://samblouir.com/chat";
// const PROVERBS_STORAGE_KEY = "proverbsExerciseState_v2"; // Optional: For state persistence

// --- Proverbs Data by Language ---
const proverbsByLanguage = {
  bn: [
    // Bengali data
    {
      id: "bn1",
      template: "অতি ___ তাঁতি ___",
      words: ["লোভে", "নষ্ট"],
      translation: "All covet all lost",
    },
    {
      id: "bn2",
      template: "চোরের মায়ের বড় ___",
      words: ["গলা"],
      translation: "A thief’s mother has the loudest voice",
    },
    {
      id: "bn3",
      template: "যত ___ তত বর্ষে না",
      words: ["গর্জে"],
      translation: "Barking dogs seldom bite",
    },
    {
      id: "bn4",
      template: "বিনা ___ বজ্রপাত",
      words: ["মেঘে"],
      translation: "A bolt from the blue",
    },
    {
      id: "bn5",
      template: "ঘর-পোড়া গরু ___ মেঘ দেখলে ভয় পায়",
      words: ["সিন্দুরে"],
      translation: "Once bitten twice shy",
    },
    {
      id: "bn6",
      template: "পাগলে কী না বলে, ___ কী না খায়",
      words: ["ছাগলে"],
      translation: "Don't think over a mad man's speech",
    },
  ],
  en: [
    // English data
    {
      id: "en1",
      template: "Don’t count your ______ before they hatch.",
      words: ["chickens"],
      translation: "Don’t assume a positive outcome before it’s guaranteed.",
    },
    {
      id: "en2",
      template: "Kill two ______ with one stone.",
      words: ["birds"],
      translation: "Accomplish two tasks at once with a single effort.",
    },
    {
      id: "en3",
      template: "A ______ for your thoughts.",
      words: ["penny"],
      translation: "A polite way to ask someone what they’re thinking.",
    },
    {
      id: "en4",
      template: "Practice makes ______.",
      words: ["perfect"],
      translation:
        "Repeated practice or effort leads to improvement or mastery.",
    },
    {
      id: "en5",
      template: "You can’t judge a ______ by its cover.",
      words: ["book"],
      translation:
        "You shouldn’t form an opinion about something/someone solely based on appearance.",
    },
    {
      id: "en6",
      template: "Better late than ______.",
      words: ["never"],
      translation:
        "It’s preferable to do something or arrive late than not at all.",
    },
    {
      id: "en7",
      template: "Actions speak louder than ______.",
      words: ["words"],
      translation: "What people do is more important than what they say.",
    },
    {
      id: "en8",
      template: "When it rains, it ______.",
      words: ["pours"],
      translation:
        "Troubles or events often happen all at once rather than being spaced out.",
    },
    {
      id: "en9",
      template: "When life gives you lemons, make ______.",
      words: ["lemonade"],
      translation:
        "Turn a negative situation into something positive or productive.",
    },
    {
      id: "en10",
      template: "Let the cat out of the ______.",
      words: ["bag"],
      translation:
        "To reveal a secret or hidden information, often accidentally.",
    },
  ],
  // Add more language codes and data arrays here: 'es': [ ... ],
};

// Defines the languages available in the UI selector
// Defines the languages available in the UI selector
const availableLanguages = [
  { code: "bn", name: "Bengali", emoji: "🇧🇩" }, // Bangladesh flag emoji
  { code: "en", name: "English", emoji: "🇺🇸" }, // US flag emoji
  // Add more language objects here with their respective flag emojis
  // Example: { code: 'es', name: 'Spanish', emoji: '🇪🇸' },
];

// --- Utility Functions ---
// Shuffle array in place (Fisher-Yates)
function shuffleArray(array) {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
}

// Clean assistant response (removes specific tags, decodes HTML entities)
function cleanAssistantResponse(rawText) {
  if (!rawText) return "";
  let cleaned = rawText.replace(/_smiles_/gi, ""); // Optional: remove custom tags
  // Decode common HTML entities that might be returned by LLMs
  let plainText = cleaned
    .replace(/&#39;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&");
  return plainText.trim();
}

// --- Reducer Actions ---
const ACTIONS = {
  START_EXERCISE: "START_EXERCISE",
  SUBMIT_ANSWER: "SUBMIT_ANSWER",
  REVEAL_ANSWER: "REVEAL_ANSWER",
  NEXT_ITEM: "NEXT_ITEM",
  FETCH_REPORT_START: "FETCH_REPORT_START",
  STREAM_REPORT_CHUNK: "STREAM_REPORT_CHUNK",
  STREAM_REPORT_FINISH: "STREAM_REPORT_FINISH",
  PARSE_AND_STORE_REPORTS: "PARSE_AND_STORE_REPORTS",
  STREAM_REPORT_ERROR: "STREAM_REPORT_ERROR",
  TRIGGER_FETCH_REPORT: "TRIGGER_FETCH_REPORT",
  RESET_EXERCISE: "RESET_EXERCISE",
  SET_USER_INPUT: "SET_USER_INPUT",
  CLEAR_FEEDBACK: "CLEAR_FEEDBACK",
  CHANGE_LANGUAGE: "CHANGE_LANGUAGE",
};

// --- Initial State Function (Language Aware) ---
const getInitialState = (languageCode = "bn") => {
  // Default language
  const proverbsForLanguage = proverbsByLanguage[languageCode] || [];
  if (proverbsForLanguage.length === 0) {
    console.warn(`No proverbs found for language: ${languageCode}`);
  }

  const shuffledIndices = shuffleArray([
    ...Array(proverbsForLanguage.length).keys(),
  ]);
  const initialProverbIndex =
    proverbsForLanguage.length > 0 ? shuffledIndices[0] : -1;
  const totalWords = proverbsForLanguage.reduce(
    (sum, p) => sum + p.words.length,
    0
  );

  return {
    selectedLanguage: languageCode,
    proverbs: proverbsForLanguage,
    shuffledIndices: shuffledIndices,
    currentShuffledIndex: 0,
    currentProverbIndex: initialProverbIndex,
    currentWordIndex: 0,
    totalWords: totalWords,
    userInput: "",
    isRevealed: false,
    feedbackMessage: "",
    isCorrect: false,
    scoreCorrect: 0,
    scoreIncorrect: 0,
    incorrectAnswers: [],
    isFinished: proverbsForLanguage.length === 0,
    isReportStreaming: false,
    rawReportContent: "",
    userReportContent: "",
    slpReportContent: "",
    reportError: null,
    fetchReportPayload: null,
  };
};

// --- Reducer ---
function reducer(state, action) {
  switch (action.type) {
    case ACTIONS.CHANGE_LANGUAGE:
      return getInitialState(action.payload);

    case ACTIONS.SET_USER_INPUT:
      return { ...state, userInput: action.payload };

    case ACTIONS.CLEAR_FEEDBACK:
      return { ...state, feedbackMessage: "", isCorrect: false };

    case ACTIONS.SUBMIT_ANSWER: {
      if (state.currentProverbIndex < 0) return state; // No proverb loaded
      const { guess } = action.payload;
      const currentProverb = state.proverbs[state.currentProverbIndex];
      const correctWord = currentProverb.words[state.currentWordIndex];
      const isGuessCorrect =
        guess.trim().toLowerCase() === correctWord.toLowerCase();

      let newIncorrectAnswers = state.incorrectAnswers;
      const alreadyLoggedOrCorrect =
        state.isCorrect ||
        state.incorrectAnswers.some(
          (err) =>
            err.proverbId === currentProverb.id &&
            err.wordIndex === state.currentWordIndex
        );

      if (!isGuessCorrect && !alreadyLoggedOrCorrect) {
        newIncorrectAnswers = [
          ...state.incorrectAnswers,
          {
            // Log details about the error
            proverbId: currentProverb.id,
            proverbText: currentProverb.template, // Log template for context
            wordIndex: state.currentWordIndex,
            missedWord: correctWord,
            guess: guess.trim(),
            language: state.selectedLanguage, // Log the language
          },
        ];
      }

      return {
        ...state,
        isCorrect: isGuessCorrect,
        feedbackMessage: isGuessCorrect
          ? "Correct!"
          : `Try again! The hidden word is not "${guess.trim()}".`,
        scoreCorrect:
          isGuessCorrect && !alreadyLoggedOrCorrect
            ? state.scoreCorrect + 1
            : state.scoreCorrect,
        scoreIncorrect:
          !isGuessCorrect && !alreadyLoggedOrCorrect
            ? state.scoreIncorrect + 1
            : state.scoreIncorrect,
        incorrectAnswers: newIncorrectAnswers,
        isRevealed: false,
      };
    }

    case ACTIONS.REVEAL_ANSWER: {
      if (state.currentProverbIndex < 0) return state; // No proverb loaded
      const currentProverb = state.proverbs[state.currentProverbIndex];
      const correctWord = currentProverb.words[state.currentWordIndex];
      const alreadyLoggedOrCorrect =
        state.isCorrect ||
        state.incorrectAnswers.some(
          (err) =>
            err.proverbId === currentProverb.id &&
            err.wordIndex === state.currentWordIndex
        );

      let newIncorrectAnswers = state.incorrectAnswers;
      if (!alreadyLoggedOrCorrect) {
        newIncorrectAnswers = [
          ...state.incorrectAnswers,
          {
            proverbId: currentProverb.id,
            proverbText: currentProverb.template,
            wordIndex: state.currentWordIndex,
            missedWord: correctWord,
            guess: "[Revealed]",
            language: state.selectedLanguage,
          },
        ];
      }

      return {
        ...state,
        isRevealed: true,
        userInput: correctWord,
        feedbackMessage: "",
        isCorrect: false,
        scoreIncorrect: !alreadyLoggedOrCorrect
          ? state.scoreIncorrect + 1
          : state.scoreIncorrect,
        incorrectAnswers: newIncorrectAnswers,
      };
    }

    case ACTIONS.NEXT_ITEM: {
      if (state.currentProverbIndex < 0) return state; // No proverb loaded
      const currentProverb = state.proverbs[state.currentProverbIndex];
      const isLastWordInProverb =
        state.currentWordIndex >= currentProverb.words.length - 1;
      const isLastProverbInList =
        state.currentShuffledIndex >= state.shuffledIndices.length - 1;

      if (isLastWordInProverb && isLastProverbInList) {
        return { ...state, isFinished: true };
      }

      let nextShuffledIndex = state.currentShuffledIndex;
      let nextProverbIndex = state.currentProverbIndex;
      let nextWordIndex = state.currentWordIndex + 1;

      if (isLastWordInProverb) {
        nextShuffledIndex = state.currentShuffledIndex + 1;
        nextProverbIndex = state.shuffledIndices[nextShuffledIndex];
        nextWordIndex = 0;
      }

      return {
        ...state,
        currentShuffledIndex: nextShuffledIndex,
        currentProverbIndex: nextProverbIndex,
        currentWordIndex: nextWordIndex,
        userInput: "",
        isRevealed: false,
        feedbackMessage: "",
        isCorrect: false,
      };
    }

    case ACTIONS.FETCH_REPORT_START:
      return {
        ...state,
        isReportStreaming: true,
        rawReportContent: "Generating reports...",
        userReportContent: "",
        slpReportContent: "",
        reportError: null,
        fetchReportPayload: action.payload,
      };

    case ACTIONS.TRIGGER_FETCH_REPORT:
      return { ...state, fetchReportPayload: null };

    case ACTIONS.STREAM_REPORT_CHUNK:
      return {
        ...state,
        rawReportContent: action.payload, // Update raw content as it streams
      };

    case ACTIONS.STREAM_REPORT_FINISH:
      // Store final raw payload; parsing happens next
      return {
        ...state,
        rawReportContent: action.payload,
      };

    case ACTIONS.PARSE_AND_STORE_REPORTS: {
      const rawContent = action.payload;
      let userReport = "";
      let slpReport = "";
      const userHeading = "## User Progress Report";
      const slpHeading = "## SLP Clinical Notes";

      const userReportStartIndex = rawContent.indexOf(userHeading);
      const slpReportStartIndex = rawContent.indexOf(slpHeading);

      if (slpReportStartIndex !== -1) {
        if (
          userReportStartIndex !== -1 &&
          userReportStartIndex < slpReportStartIndex
        ) {
          userReport = rawContent
            .substring(
              userReportStartIndex + userHeading.length,
              slpReportStartIndex
            )
            .trim();
        } else {
          userReport = rawContent.substring(0, slpReportStartIndex).trim();
        }
        slpReport = rawContent
          .substring(slpReportStartIndex + slpHeading.length)
          .trim();
      } else if (userReportStartIndex !== -1) {
        console.warn(
          "Could not find SLP heading. Putting all content after User heading into User Report."
        );
        userReport = rawContent
          .substring(userReportStartIndex + userHeading.length)
          .trim();
        slpReport = "[SLP Notes section not found in response]";
      } else {
        console.warn(
          "Could not find User or SLP headings. Displaying all content as user report."
        );
        userReport = rawContent;
        slpReport = "[Report sections not found in response]";
      }

      return {
        ...state,
        userReportContent: cleanAssistantResponse(userReport),
        slpReportContent: cleanAssistantResponse(slpReport),
        isReportStreaming: false,
        reportError: null,
        rawReportContent: "", // Clear raw content
      };
    }

    case ACTIONS.STREAM_REPORT_ERROR:
      return {
        ...state,
        rawReportContent: "",
        userReportContent: "",
        slpReportContent: "",
        isReportStreaming: false,
        reportError: action.payload,
      };

    case ACTIONS.RESET_EXERCISE:
      // Reset using the currently selected language
      return getInitialState(state.selectedLanguage);

    default:
      console.warn(`Unhandled action type: ${action.type}`);
      return state;
  }
}

// --- Component ---
function ProverbsExercise() {
  // Initialize state with default language
  const [state, dispatch] = useReducer(reducer, undefined, () =>
    getInitialState("bn")
  );
  const inputRef = useRef(null);

  const {
    selectedLanguage,
    proverbs,
    currentProverbIndex,
    currentWordIndex,
    userInput,
    isRevealed,
    feedbackMessage,
    isCorrect,
    scoreCorrect,
    scoreIncorrect,
    incorrectAnswers,
    isFinished,
    isReportStreaming,
    userReportContent,
    slpReportContent,
    reportError,
    fetchReportPayload,
    totalWords,
    shuffledIndices, // Needed for progress calculation
    currentShuffledIndex,
  } = state;

  // Get the current proverb object safely
  const currentProverb =
    currentProverbIndex >= 0 && currentProverbIndex < proverbs.length
      ? proverbs[currentProverbIndex]
      : null;

  const correctWord = currentProverb
    ? currentProverb.words[currentWordIndex]
    : "";

  // --- Effect for Fetching Progress Report ---
  useEffect(() => {
    if (!fetchReportPayload) return;

    const { prompt } = fetchReportPayload;
    let currentRawBotResponse = "";
    dispatch({ type: ACTIONS.TRIGGER_FETCH_REPORT }); // Consume payload

    const performFetch = async () => {
      const formData = new FormData();
      formData.append("user_input", prompt);
      // formData.append("history", JSON.stringify([])); // Add history if API needs it

      try {
        const response = await fetch(CHAT_API_URL, {
          method: "POST",
          body: formData,
        });
        if (!response.ok || !response.body) {
          let errorBody = "API Error";
          try {
            errorBody = await response.text();
          } catch (_) {}
          throw new Error(
            `HTTP error! Status: ${response.status}. ${errorBody}`
          );
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();

        const processStream = () => {
          reader
            .read()
            .then(({ done, value }) => {
              if (done) {
                const finalRawText =
                  currentRawBotResponse.replace(/\[DONE\]$/, "").trim() || " ";
                dispatch({
                  type: ACTIONS.STREAM_REPORT_FINISH,
                  payload: finalRawText,
                });
                // Parse immediately after finish
                dispatch({
                  type: ACTIONS.PARSE_AND_STORE_REPORTS,
                  payload: finalRawText,
                });
                return;
              }
              const chunk = decoder.decode(value, { stream: true });
              currentRawBotResponse += chunk;
              const progressiveRawText =
                currentRawBotResponse.replace(/\[DONE\]$/, "").trimEnd() ||
                "\u00A0";
              // Update raw state during streaming
              dispatch({
                type: ACTIONS.STREAM_REPORT_CHUNK,
                payload: progressiveRawText,
              });
              processStream();
            })
            .catch((streamError) => {
              console.error("Stream reading error:", streamError);
              dispatch({
                type: ACTIONS.STREAM_REPORT_ERROR,
                payload: `Stream Error: ${streamError.message}`,
              });
            });
        };
        processStream();
      } catch (fetchError) {
        console.error("Fetch operation error:", fetchError);
        dispatch({
          type: ACTIONS.STREAM_REPORT_ERROR,
          payload: `Workspace Error: ${fetchError.message}`,
        });
      }
    };

    performFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchReportPayload, dispatch]); // Added dispatch dependency

  // Focus the input field
  useEffect(() => {
    if (!isFinished && !isRevealed && currentProverb && inputRef.current) {
      inputRef.current.focus();
    }
  }, [
    currentProverbIndex,
    currentWordIndex,
    isFinished,
    isRevealed,
    currentProverb,
  ]); // Depend on currentProverb existence

  // --- Event Handlers ---
  const handleLanguageChange = (langCode) => {
    if (langCode !== selectedLanguage) {
      console.log(`Changing language to: ${langCode}`);
      // Optional: Confirm before changing language if progress exists?
      // if (window.confirm("Changing language will reset progress. Continue?")) {
      dispatch({ type: ACTIONS.CHANGE_LANGUAGE, payload: langCode });
      // }
    }
  };

  const handleInputChange = (event) => {
    dispatch({ type: ACTIONS.SET_USER_INPUT, payload: event.target.value });
    if (feedbackMessage) {
      dispatch({ type: ACTIONS.CLEAR_FEEDBACK });
    }
  };

  const handleCheck = useCallback(() => {
    if (!userInput.trim() || !currentProverb) return;
    dispatch({ type: ACTIONS.SUBMIT_ANSWER, payload: { guess: userInput } });
  }, [userInput, currentProverb, dispatch]); // Depend on currentProverb

  const handleReveal = useCallback(() => {
    if (!currentProverb) return;
    dispatch({ type: ACTIONS.REVEAL_ANSWER });
  }, [currentProverb, dispatch]); // Depend on currentProverb

  const handleNext = useCallback(() => {
    if (!currentProverb) return;
    dispatch({ type: ACTIONS.NEXT_ITEM });
  }, [currentProverb, dispatch]); // Depend on currentProverb

  const handleGetReport = useCallback(() => {
    if (isReportStreaming || incorrectAnswers.length === 0) return;

    const errorList = incorrectAnswers
      // Filter by current language just in case state is mixed somehow (shouldn't be)
      .filter((err) => err.language === selectedLanguage)
      .map(
        (err) =>
          `- Proverb: "${err.proverbText}", Missed Word: "${err.missedWord}", Guessed: "${err.guess}"`
      )
      .join("\n");

    if (!errorList) {
      console.log("No errors logged for the current language session.");
      return; // Don't fetch report if no errors for this session
    }

    const prompt = `The user encountered difficulties with the following proverbs/idioms during an exercise in ${
      availableLanguages.find((l) => l.code === selectedLanguage)?.name ||
      selectedLanguage
    }:\n${errorList}\n\nPlease generate two reports based on these errors, clearly separated by the specified headings:
  
  ## User Progress Report
  Write a brief, encouraging progress report for the user (using "you"). Keep the tone positive and supportive for a language learner. Suggest 1-2 specific areas or types of phrases the user might focus on for improvement based ONLY on these examples. Avoid overly clinical language. End the user report here.
  
  ## SLP Clinical Notes
  Provide concise clinical notes for an SLP based on the errors above for the ${
    availableLanguages.find((l) => l.code === selectedLanguage)?.name ||
    selectedLanguage
  } language. Analyze potential error patterns (e.g., semantic vs. phonological confusion, issues with figurative language). Suggest potential underlying language processing difficulties indicated by these specific errors and recommend 1-2 specific follow-up tasks or assessment areas for the SLP to consider. Use appropriate clinical terminology.`;

    const payload = { prompt: prompt };
    dispatch({ type: ACTIONS.FETCH_REPORT_START, payload: payload });
  }, [incorrectAnswers, isReportStreaming, selectedLanguage, dispatch]); // Added selectedLanguage dependency

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        if (isFinished || !currentProverb) return; // No action if finished or no proverb

        if (!isCorrect && !isRevealed) {
          handleCheck();
        } else {
          handleNext();
        }
      }
    },
    [isFinished, isCorrect, isRevealed, currentProverb, handleCheck, handleNext]
  ); // Added currentProverb dependency

  const handleBack = () => window.history.back();
  const handleReset = () => {
    // Optional: Confirm before resetting?
    // if (window.confirm("Restart exercise in the current language?")) {
    dispatch({ type: ACTIONS.RESET_EXERCISE });
    // }
  };

  // --- Rendering Logic ---
  const renderProverb = () => {
    if (!currentProverb) {
      return <div style={styles.proverbDisplay}>Loading proverb...</div>; // Or handle empty state better
    }
    const parts = currentProverb.template.split("___");
    const elements = [];
    parts.forEach((part, index) => {
      elements.push(<span key={`part-${index}`}>{part}</span>);
      if (index < currentProverb.words.length) {
        const wordIsCurrentlyTargeted = index === currentWordIndex;
        const isHighlighted =
          wordIsCurrentlyTargeted && (isRevealed || isCorrect);
        // Determine if word was previously answered correctly for display purposes
        // Note: `isCorrect` only reflects the *last* check state. A better check might involve `incorrectAnswers` list.
        const wordToShow =
          isRevealed && wordIsCurrentlyTargeted
            ? correctWord
            : index < currentWordIndex || (isCorrect && wordIsCurrentlyTargeted)
            ? currentProverb.words[index]
            : "[...]"; // Placeholder for upcoming words

        if (wordIsCurrentlyTargeted && !isRevealed) {
          elements.push(
            <input
              key={`input-${index}`}
              ref={inputRef}
              type="text"
              value={userInput}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              style={styles.inputWord}
              aria-label={`Enter missing word ${index + 1}`}
              disabled={isRevealed || isFinished} // Keep disabled logic
            />
          );
        } else {
          elements.push(
            <span
              key={`filled-${index}`}
              style={
                isHighlighted
                  ? styles.revealedWordHighlight
                  : styles.revealedWord
              }
            >
              {wordToShow}
            </span>
          );
        }
      }
    });
    return <div style={styles.proverbDisplay}>{elements}</div>;
  };

  const renderFinishedState = () => (
    <div style={styles.finishedContainer}>
      <h2 style={styles.finishedTitle}>Exercise Complete!</h2>

      {/* Score Card */}
      <div style={styles.scoreCard}>
        <div style={styles.scoreText}>
          Final Score (
          {availableLanguages.find((l) => l.code === selectedLanguage)?.name ||
            selectedLanguage}
          )
        </div>
        <div style={styles.scoreValue}>
          {scoreCorrect} / {totalWords > 0 ? totalWords : "-"}{" "}
          {/* Handle zero total words */}
        </div>
        {scoreIncorrect > 0 && (
          <div style={styles.scoreDetails}>
            ({scoreIncorrect} incorrect or revealed)
          </div>
        )}
      </div>

      {/* Report Section - Show only if there were proverbs and errors */}
      {totalWords > 0 && incorrectAnswers.length > 0 && (
        <div style={styles.reportSection}>
          <button
            style={{
              ...styles.button,
              ...styles.buttonGetReport,
              ...(isReportStreaming ? styles.buttonDisabled : {}),
            }}
            onClick={handleGetReport}
            disabled={isReportStreaming}
          >
            <div style={verticalButtonStyle}>
              <Icon.MdAssignment size="2em" />
              <span>{isReportStreaming ? "GENERATING..." : "GET REPORTS"}</span>
            </div>
          </button>

          {/* Display Area for Reports */}
          {isReportStreaming && !reportError && (
            <div style={styles.reportPlaceholder}>
              <i>Generating reports, please wait...</i>
            </div>
          )}
          {reportError && (
            <div style={styles.errorBox}>
              <p style={styles.errorMessage}>
                Error fetching report: {reportError}
              </p>
              <button
                onClick={() =>
                  dispatch({ type: ACTIONS.STREAM_REPORT_ERROR, payload: null })
                }
                style={styles.clearErrorButton}
              >
                Clear Error
              </button>
            </div>
          )}

          {!isReportStreaming &&
            !reportError &&
            (userReportContent || slpReportContent) && (
              <div style={styles.reportsDisplayContainer}>
                {/* User Report Box */}
                {userReportContent && (
                  <div style={styles.reportBox}>
                    <h3 style={styles.reportHeading}>User Progress Report</h3>
                    <div
                      style={styles.reportContent}
                      dangerouslySetInnerHTML={{
                        __html: marked.parse(userReportContent || ""),
                      }}
                    ></div>
                  </div>
                )}
                {/* SLP Report Box */}
                {slpReportContent && (
                  <div style={styles.reportBox}>
                    <h3 style={styles.reportHeading}>SLP Clinical Notes</h3>
                    <div
                      style={styles.reportContent}
                      dangerouslySetInnerHTML={{
                        __html: marked.parse(slpReportContent || ""),
                      }}
                    ></div>
                  </div>
                )}
              </div>
            )}
        </div>
      )}
      {totalWords > 0 && incorrectAnswers.length === 0 && (
        <p style={styles.perfectScoreMessage}>
          Excellent work! You got everything correct.
        </p>
      )}
      {totalWords === 0 && ( // Message if no proverbs were available for the language
        <p style={styles.noProverbsMessage}>
          No proverbs were loaded for the selected language.
        </p>
      )}
    </div>
  );

  // --- Main Component Return ---
  return (
    <div style={styles.container}>
      {/* Language Selector Row */}
      <div style={styles.languageSelectorContainer}>
        {availableLanguages.map((lang) => (
          <button
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            style={{
              ...styles.languageButton,
              ...(selectedLanguage === lang.code
                ? styles.languageButtonActive
                : {}),
            }}
            disabled={selectedLanguage === lang.code || isReportStreaming}
          >
            {/* Add emoji span before the name */}
            <span
              style={styles.flagEmoji}
              role="img"
              aria-label={`${lang.name} flag`}
            >
              {lang.emoji}{" "}
            </span>
            {lang.name}
          </button>
        ))}
      </div>
      {/* Exercise Area or Finished State */}
      <div style={styles.exerciseArea}>
        {!isFinished ? (
          <>
            {renderProverb()}
            {currentProverb && (
              <div style={styles.translationText}>
                ({currentProverb.translation})
              </div>
            )}
            <div
              style={{
                ...styles.feedbackMessage,
                ...(isCorrect
                  ? styles.feedbackCorrect
                  : feedbackMessage
                  ? styles.feedbackIncorrect
                  : {}),
              }}
            >
              {feedbackMessage || "\u00A0"}
            </div>
            {totalWords > 0 &&
              currentProverb && ( // Show progress only if applicable
                <div style={styles.progressIndicator}>
                  Item:{" "}
                  {currentShuffledIndex *
                    (proverbs[shuffledIndices[currentShuffledIndex]]?.words
                      .length || 1) +
                    currentWordIndex +
                    1}{" "}
                  / {totalWords}
                </div>
              )}
          </>
        ) : (
          renderFinishedState()
        )}
      </div>
      {/* Control Panel */}
      <div style={styles.controlPanel}>
        <div style={styles.buttonRow}>
          {/* Back Button */}
          <button
            style={{ ...styles.button, ...styles.buttonBack }}
            onClick={handleBack}
            disabled={isReportStreaming} // Disable during report generation
          >
            <div style={verticalButtonStyle}>
              <Icon.MdArrowBackIos size="2em" />
              <span>BACK</span>
            </div>
          </button>

          {/* Reset Button (Restart Exercise in current language) */}
          <button
            style={{
              ...styles.button,
              ...styles.buttonReset,
              ...(isReportStreaming ? styles.buttonDisabled : {}),
            }}
            onClick={handleReset}
            disabled={isReportStreaming} // Disable during report generation
          >
            <div style={verticalButtonStyle}>
              <Icon.MdRefresh size="2em" />
              <span>RESTART</span>
            </div>
          </button>

          {/* Conditional Buttons for Exercise State - Disable if no proverbs or streaming */}
          {!isFinished && proverbs.length > 0 && (
            <>
              {/* Check Button */}
              <button
                style={{
                  ...styles.button,
                  ...styles.buttonCheck,
                  ...(!userInput.trim() ||
                  isRevealed ||
                  isCorrect ||
                  isReportStreaming
                    ? styles.buttonDisabled
                    : {}),
                }}
                onClick={handleCheck}
                disabled={
                  !userInput.trim() ||
                  isRevealed ||
                  isCorrect ||
                  isReportStreaming
                }
              >
                <div style={verticalButtonStyle}>
                  <Icon.MdCheckCircleOutline size="2em" />
                  <span>CHECK</span>
                </div>
              </button>

              {/* Reveal Button */}
              <button
                style={{
                  ...styles.button,
                  ...styles.buttonReveal,
                  ...(isRevealed || isReportStreaming
                    ? styles.buttonDisabled
                    : {}),
                }}
                onClick={handleReveal}
                disabled={isRevealed || isReportStreaming}
              >
                <div style={verticalButtonStyle}>
                  <Icon.MdVisibility size="2em" />
                  <span>REVEAL</span>
                </div>
              </button>

              {/* Next Button */}
              <button
                style={{
                  ...styles.button,
                  ...styles.buttonNext,
                  ...(!(isCorrect || isRevealed) || isReportStreaming
                    ? styles.buttonDisabled
                    : {}),
                }}
                onClick={handleNext}
                disabled={!(isCorrect || isRevealed) || isReportStreaming}
              >
                <div style={verticalButtonStyle}>
                  <Icon.MdSkipNext size="2em" />
                  <span>NEXT</span>
                </div>
              </button>
            </>
          )}
        </div>
      </div>{" "}
      {/* End Control Panel */}
    </div> // End Container
  );
}

// --- Styles ---
const verticalButtonStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "2px",
};

const styles = {
  container: {
    maxWidth: "1080px",
    margin: "20px auto",
    border: "4px solid #000",
    borderRadius: "16px",
    overflow: "hidden",
    fontFamily: "Arial, sans-serif",
    boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    display: "flex",
    flexDirection: "column",
    minHeight: "600px",
    padding: "0",
  },
  languageSelectorContainer: {
    display: "flex",
    justifyContent: "center",
    gap: "15px",
    padding: "15px 20px",
    backgroundColor: "#e9ecef",
    borderBottom: "2px solid #ced4da",
    flexShrink: 0,
  },
  languageButton: {
    padding: "8px 18px",
    fontSize: "1.1em",
    fontWeight: "bold",
    borderRadius: "20px",
    border: "2px solid #adb5bd",
    backgroundColor: "#fff",
    color: "#495057",
    cursor: "pointer",
    transition: "all 0.3s ease",
    display: "inline-flex", // Use flex to align emoji and text
    alignItems: "center", // Vertically center emoji and text
    gap: "8px", // Add space between emoji and text
  },
  flagEmoji: {
    fontSize: "1.5em", // Slightly larger emoji if desired
    // marginRight: '8px', // Alternative way to add space if not using gap
  },
  languageButtonActive: {
    backgroundColor: "#007bff",
    color: "#fff",
    borderColor: "#0056b3",
    cursor: "default",
    boxShadow: "inset 0 2px 4px rgba(0,0,0,0.1)",
  },
  exerciseArea: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: "20px 20px 10px 20px",
    textAlign: "center",
    width: "100%",
  },
  proverbDisplay: {
    fontSize: "2.5em",
    lineHeight: "1.6",
    margin: "20px 0",
    color: "#333",
    fontWeight: "bold",
    minHeight: "3em", // Reserve space even when loading
  },
  inputWord: {
    display: "inline-block",
    width: "150px",
    maxWidth: "25%", // Prevent excessive width on long proverbs
    fontSize: "1em",
    border: "none",
    borderBottom: "2px solid #888",
    textAlign: "center",
    margin: "0 5px",
    padding: "5px 2px",
    backgroundColor: "transparent",
    outline: "none",
  },
  revealedWord: {
    display: "inline-block",
    fontWeight: "normal",
    color: "#555",
    borderBottom: "2px solid transparent",
    padding: "5px 2px",
    margin: "0 5px",
    fontStyle: "italic",
  },
  revealedWordHighlight: {
    display: "inline-block",
    fontWeight: "bold",
    color: "#007bff",
    borderBottom: "2px solid transparent",
    padding: "5px 2px",
    margin: "0 5px",
  },
  translationText: {
    fontSize: "1.2em",
    color: "#555",
    marginTop: "10px",
    fontStyle: "italic",
  },
  feedbackMessage: {
    marginTop: "15px",
    fontSize: "1.4em",
    fontWeight: "bold",
    minHeight: "2em",
    color: "#555",
  },
  feedbackCorrect: { color: "#28a745" },
  feedbackIncorrect: { color: "#dc3545" },
  controlPanel: {
    padding: "20px",
    backgroundColor: "#f8f9fa",
    borderTop: "2px solid #dee2e6",
    marginTop: "auto",
    flexShrink: 0,
  },
  buttonRow: {
    display: "flex",
    gap: "16px",
    justifyContent: "center",
    flexWrap: "wrap",
  },
  button: {
    padding: "12px 16px",
    borderRadius: "16px",
    cursor: "pointer",
    fontSize: "1.4em",
    fontWeight: "bold",
    color: "white",
    textAlign: "center",
    border: "4px solid #000",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    transition: "all 0.3s ease", // Faster transition
    minHeight: "60px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.5)",
    flex: "1 1 auto",
    minWidth: "120px",
    maxWidth: "180px",
  },
  buttonBack: { backgroundColor: "#007bff", borderColor: "#0056b3" },
  buttonReset: { backgroundColor: "#6f42c1", borderColor: "#5a349a" },
  buttonCheck: { backgroundColor: "#28a745", borderColor: "#1c7430" },
  buttonReveal: {
    backgroundColor: "#ffc107",
    borderColor: "#c69500",
    color: "#333",
  },
  buttonNext: { backgroundColor: "#17a2b8", borderColor: "#117a8b" },
  buttonGetReport: {
    backgroundColor: "#fd7e14",
    borderColor: "#c76411",
    minWidth: "200px",
    flexShrink: 0,
  },
  buttonDisabled: {
    backgroundColor: "#6c757d",
    borderColor: "#5a6268",
    cursor: "not-allowed",
    opacity: 0.65,
    boxShadow: "none", // Remove shadow when disabled
  },
  // --- Styles for Finished State ---
  finishedContainer: {
    width: "100%",
    padding: "20px 30px 30px 30px",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    flexGrow: 1,
  },
  finishedTitle: {
    color: "#333",
    marginBottom: "20px",
  },
  scoreCard: {
    backgroundColor: "#e9ecef",
    border: "1px solid #ced4da",
    borderRadius: "12px",
    padding: "20px 30px",
    margin: "10px 0 30px 0",
    display: "inline-block",
    minWidth: "250px",
    boxShadow: "0 4px 8px rgba(0,0,0,0.05)",
  },
  scoreText: {
    fontSize: "1.1em",
    color: "#495057",
    marginBottom: "5px",
  },
  scoreValue: {
    fontSize: "2.5em",
    fontWeight: "bold",
    color: "#007bff",
  },
  scoreDetails: {
    fontSize: "0.9em",
    color: "#6c757d",
    marginTop: "5px",
  },
  reportSection: {
    marginTop: "20px",
    paddingTop: "20px",
    borderTop: "2px solid #eee",
    width: "100%",
    maxWidth: "800px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "20px",
  },
  reportsDisplayContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    width: "100%",
    marginTop: "10px",
  },
  reportPlaceholder: {
    color: "#666",
    fontStyle: "italic",
    padding: "20px",
    backgroundColor: "#f8f9fa",
    border: "1px dashed #ccc",
    borderRadius: "8px",
    width: "100%",
    textAlign: "center",
  },
  errorBox: {
    border: "2px solid #dc3545",
    backgroundColor: "#f8d7da",
    padding: "15px",
    borderRadius: "8px",
    width: "100%",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "10px",
  },
  errorMessage: {
    color: "#721c24",
    fontWeight: "normal",
    margin: 0,
  },
  clearErrorButton: {
    padding: "5px 10px",
    fontSize: "0.9em",
    color: "#721c24",
    backgroundColor: "transparent",
    border: "1px solid #721c24",
    borderRadius: "4px",
    cursor: "pointer",
    marginTop: "5px",
    ":hover": {
      backgroundColor: "rgba(114, 28, 36, 0.1)",
    },
  },
  reportBox: {
    border: "1px solid #dee2e6",
    borderRadius: "12px",
    backgroundColor: "#fff",
    padding: "15px 20px",
    textAlign: "left",
    width: "100%",
    boxShadow: "0 2px 4px rgba(0,0,0,0.03)",
  },
  reportHeading: {
    marginTop: "0",
    marginBottom: "10px",
    fontSize: "1.3em",
    color: "#495057",
    borderBottom: "1px solid #eee",
    paddingBottom: "5px",
  },
  reportContent: {
    lineHeight: "1.6",
    whiteSpace: "pre-wrap",
    color: "#212529",
    fontSize: "1em",
    // Target links within the parsed markdown
    "& a": {
      color: "#007bff",
      textDecoration: "underline",
    },
  },
  perfectScoreMessage: {
    fontSize: "1.3em",
    color: "#155724",
    backgroundColor: "#d4edda",
    border: "1px solid #c3e6cb",
    borderRadius: "8px",
    padding: "15px 20px",
    display: "inline-block",
    marginTop: "20px",
    fontWeight: "bold",
  },
  progressIndicator: {
    marginTop: "15px",
    fontSize: "1em",
    color: "#6c757d",
    width: "100%",
    textAlign: "center",
    minHeight: "1.2em", // Reserve space
  },
  noProverbsMessage: {
    fontSize: "1.2em",
    color: "#6c757d",
    fontStyle: "italic",
    marginTop: "30px",
    padding: "20px",
    backgroundColor: "#f8f9fa",
    borderRadius: "8px",
    border: "1px dashed #ccc",
  },
};

export default ProverbsExercise;
